// src/components/VerificadorDocumento.js
// Props:
//   titulo, descripcion — texto del campo
//   cedula, nombres, apellidos — datos del formulario para cruzar con el documento
//   onValido(file, resultado)  — llamado cuando el backend aprueba
//   onInvalido()               — llamado cuando se rechaza o elimina

import React, { useState, useRef } from 'react';
import {
  Box, Typography, LinearProgress, Alert, Button, Chip, Tooltip,
} from '@mui/material';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import ErrorIcon          from '@mui/icons-material/Error';
import CloudUploadIcon    from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import HourglassIcon      from '@mui/icons-material/HourglassEmpty';
import LockIcon           from '@mui/icons-material/Lock';
import QrCodeIcon         from '@mui/icons-material/QrCode';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import { verificarDocumento } from '../api/verificacionApi';

const VERDE = '#2e7d32';
const ROJO  = '#c62828';
const AZUL  = '#1565c0';
const GRIS  = '#9e9e9e';

// Pasos que muestra la UI mientras espera la respuesta del backend
const PASOS_UI = [
  { id: 'codigo_barras', label: 'Leyendo QR y código de barras',      icon: <QrCodeIcon sx={{ fontSize: 15 }}/> },
  { id: 'ocr',          label: 'OCR — extrayendo texto del documento', icon: <DocumentScannerIcon sx={{ fontSize: 15 }}/> },
  { id: 'cruce_datos',  label: 'Cruzando datos con el formulario',     icon: <CheckCircleIcon sx={{ fontSize: 15 }}/> },
  { id: 'api_externa',  label: 'Consultando API de Registraduría',     icon: <LockIcon sx={{ fontSize: 15 }}/> },
];

const colorEstado = (estado) => ({
  OK:       VERDE,
  Parcial:  '#e65100',
  Error:    ROJO,
  Pendiente: GRIS,
  Omitido:  GRIS,
  SinCodigo: '#e65100',
  SinTexto:  '#e65100',
}[estado] || GRIS);

const iconoEstado = (estado) => {
  if (estado === 'OK')                    return <CheckCircleIcon sx={{ fontSize: 16, color: VERDE }}/>;
  if (estado === 'Error')                 return <ErrorIcon sx={{ fontSize: 16, color: ROJO }}/>;
  if (estado === 'Pendiente' || estado === 'Omitido')
    return <HourglassIcon sx={{ fontSize: 16, color: GRIS }}/>;
  return <HourglassIcon sx={{ fontSize: 16, color: '#e65100' }}/>;
};

export default function VerificadorDocumento({
  titulo, descripcion, cedula = '', nombres = '', apellidos = '',
  tipoDocumento = 'cedula',  // cedula | rut | antecedentes | bancario | generico
  onValido, onInvalido,
}) {
  const inputRef  = useRef(null);
  const [file,    setFile]    = useState(null);
  const [preview, setPreview] = useState(null);
  const [estado,  setEstado]  = useState('idle');   // idle | subiendo | valido | invalido
  const [progreso, setProgreso] = useState(0);
  const [pasoAnimado, setPasoAnimado] = useState(-1);
  const [resultado, setResultado] = useState(null);
  const [error,   setError]   = useState('');

  const limpiar = () => {
    setFile(null); setPreview(null); setEstado('idle');
    setProgreso(0); setPasoAnimado(-1); setResultado(null); setError('');
    if (inputRef.current) inputRef.current.value = '';
    onInvalido?.();
  };

  const procesarArchivo = async (f) => {
    if (!f) return;

    // Validaciones locales básicas antes de enviar
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['jpg','jpeg','png','pdf'].includes(ext)) {
      setError(`Formato ".${ext}" no permitido. Use JPG, PNG o PDF.`);
      setEstado('invalido'); onInvalido?.(); return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError('El archivo supera los 10MB permitidos.');
      setEstado('invalido'); onInvalido?.(); return;
    }
    if (f.size < 5 * 1024) {
      setError('El archivo es demasiado pequeño. Puede estar en blanco o corrupto.');
      setEstado('invalido'); onInvalido?.(); return;
    }

    setFile(f); setError(''); setEstado('subiendo'); setProgreso(0);

    // Preview para imágenes
    if (f.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = e => setPreview(e.target.result);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }

    // Animación de pasos mientras el backend trabaja
    let pasoIdx = 0;
    const intervalo = setInterval(() => {
      if (pasoIdx < PASOS_UI.length) {
        setPasoAnimado(pasoIdx);
        setProgreso(Math.round(((pasoIdx + 1) / PASOS_UI.length) * 85));
        pasoIdx++;
      }
    }, 700);

    try {
      const res = await verificarDocumento(f, cedula, nombres, apellidos, tipoDocumento);
      clearInterval(intervalo);
      setPasoAnimado(-1);
      setProgreso(100);
      setResultado(res);

      if (res.valido) {
        setEstado('valido');
        onValido?.(f, res);
      } else {
        setEstado('invalido');
        setError(res.mensaje || 'Verificación incompleta — se requiere revisión manual.');
        onInvalido?.();
      }
    } catch (e) {
      clearInterval(intervalo);
      setPasoAnimado(-1);
      setProgreso(0);
      // Si el servicio de verificación falla, NO se acepta el documento.
      // Antes se dejaba pasar "para revisión manual" — eso permitía subir
      // cualquier archivo si el backend estaba caído (backdoor).
      setResultado(null);
      setEstado('invalido');
      // Antes esto mostraba SIEMPRE el mismo texto genérico ("verifica tu
      // conexión") sin importar la causa real, y el detalle real (status +
      // mensaje del backend) solo quedaba en consola — invisible para
      // cualquiera que no tuviera devtools abierto. verificarDocumento() ya
      // adjunta e.status y e.data al error; se usa acá en vez de descartarlo.
      const detalle = e.data?.mensaje || e.message || '';
      const prefijo = tipoDocumento === 'cedula'
        ? 'No se pudo verificar la cédula.'
        : 'No se pudo verificar el documento.';
      setError(
        e.status
          ? `${prefijo} (${e.status}) ${detalle}`.trim()
          : `${prefijo} No se pudo contactar el servidor — revisa tu conexión. ${detalle}`.trim()
      );
      onInvalido?.();
      console.error('Backend verificación no disponible:', { status: e.status, data: e.data, message: e.message });
    }
  };

  const esPDF = file?.name?.toLowerCase().endsWith('.pdf');

  return (
    <Box>
      {titulo && (
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a3a1a', mb: 0.5 }}>
          {titulo}
        </Typography>
      )}
      {descripcion && (
        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1.5 }}>
          {descripcion}
        </Typography>
      )}

      {/* ── IDLE: zona de carga ── */}
      {estado === 'idle' && (
        <Box
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); procesarArchivo(e.dataTransfer.files[0]); }}
          onClick={() => inputRef.current?.click()}
          sx={{
            border: '2px dashed #b0c4b8', borderRadius: 2, p: 4,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            cursor: 'pointer', background: '#fafcfa', transition: 'all 0.2s',
            '&:hover': { background: '#e8f5e9', borderColor: VERDE },
          }}
        >
          <input ref={inputRef} type="file" hidden
            accept="image/jpeg,image/png,image/jpg,application/pdf"
            onChange={e => procesarArchivo(e.target.files[0])}/>
          <CloudUploadIcon sx={{ fontSize: 48, color: '#b0c4b8', mb: 1.5 }}/>
          <Typography sx={{ fontWeight: 600, color: '#555', mb: 0.5 }}>
            Arrastra aquí o haz clic para seleccionar
          </Typography>
          <Typography variant="caption" sx={{ color: '#aaa', mb: 1.5 }}>
            JPG, PNG o PDF — máximo 10 MB
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip icon={<QrCodeIcon sx={{ fontSize: 14 }}/>} label="Lee QR / PDF417" size="small"
              sx={{ fontSize: '0.65rem', background: '#e3f2fd', color: AZUL }}/>
            <Chip icon={<DocumentScannerIcon sx={{ fontSize: 14 }}/>} label="OCR automático" size="small"
              sx={{ fontSize: '0.65rem', background: '#e8f5e9', color: VERDE }}/>
          </Box>
        </Box>
      )}

      {/* ── SUBIENDO / VERIFICANDO ── */}
      {estado === 'subiendo' && (
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 3 }}>
          {/* Miniatura */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
            {preview
              ? <Box component="img" src={preview} alt="doc"
                  sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1, border: '1px solid #e0e0e0' }}/>
              : <Box sx={{ width: 64, height: 64, borderRadius: 1, background: '#f5f5f5',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e0e0e0' }}>
                  <InsertDriveFileIcon sx={{ fontSize: 32, color: '#bbb' }}/>
                </Box>
            }
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{file?.name}</Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>
                {file ? (file.size / 1024).toFixed(1) + ' KB' : ''}
              </Typography>
            </Box>
          </Box>

          {/* Pasos animados */}
          <Box sx={{ mb: 2 }}>
            {PASOS_UI.map((p, i) => {
              const hecho  = i < pasoAnimado;
              const activo = i === pasoAnimado;
              return (
                <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1 }}>
                  {hecho
                    ? <CheckCircleIcon sx={{ fontSize: 18, color: VERDE, flexShrink: 0 }}/>
                    : <Box sx={{
                        width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                        border: `2px solid ${activo ? AZUL : '#e0e0e0'}`,
                        background: activo ? AZUL : 'transparent',
                        animation: activo ? 'pulseDoc 1s infinite' : 'none',
                      }}/>
                  }
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ color: hecho ? VERDE : activo ? AZUL : '#ccc' }}>{p.icon}</Box>
                    <Typography variant="body2" sx={{
                      fontSize: '0.8rem',
                      color: hecho ? VERDE : activo ? AZUL : '#ccc',
                      fontWeight: hecho || activo ? 600 : 400,
                    }}>
                      {p.label}{hecho ? ' ✓' : activo ? '…' : ''}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          <LinearProgress variant="determinate" value={progreso}
            sx={{ height: 6, borderRadius: 3,
              '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg,${AZUL},${VERDE})`, borderRadius: 3 },
              background: '#e8f5e9' }}/>
          <Typography variant="caption" sx={{ color: '#888', display: 'block', textAlign: 'right', mt: 0.5 }}>
            {progreso}%
          </Typography>
        </Box>
      )}

      {/* ── VÁLIDO ── */}
      {estado === 'valido' && file && (
        <Box sx={{ border: `2px solid ${VERDE}`, borderRadius: 2, p: 2.5, background: '#f0fdf0' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {preview
              ? <Box component="img" src={preview} alt="doc"
                  sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1.5,
                    border: `2px solid ${VERDE}`, flexShrink: 0 }}/>
              : <Box sx={{ width: 72, height: 72, borderRadius: 1.5, background: '#e8f5e9',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: `2px solid ${VERDE}`, flexShrink: 0 }}>
                  <InsertDriveFileIcon sx={{ fontSize: 36, color: VERDE }}/>
                </Box>
            }
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <CheckCircleIcon sx={{ color: VERDE, fontSize: 20 }}/>
                <Typography sx={{ fontWeight: 700, color: VERDE, fontSize: '0.9rem' }}>
                  Documento verificado
                </Typography>
                {resultado?.confianza != null && (
                  <Chip label={`${resultado.confianza}% confianza`} size="small" color="success"
                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}/>
                )}
                {(!resultado || resultado.mensajeAdicional) && (
                  <Chip
                    label={resultado?.mensajeAdicional ? 'Pendiente revisión ⚠️' : 'Revisión manual'}
                    size="small" color="warning"
                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}/>
                )}
              </Box>
              <Typography variant="caption" sx={{ color: '#555', display: 'block', mb: 0.3 }}>
                {file.name}
              </Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>
                {(file.size / 1024).toFixed(1)} KB
              </Typography>
            </Box>
          </Box>

          {/* Mensaje adicional para revisión manual (ej: Contraloría) */}
          {resultado?.mensajeAdicional && (
            <Box sx={{ mt:1.5, p:1.5, borderRadius:1.5, background:'#fff8e1', border:'1px solid #ffe082', display:'flex', gap:1, alignItems:'flex-start' }}>
              <span style={{ fontSize:16, flexShrink:0 }}>⚠️</span>
              <Box>
                <Typography variant="caption" sx={{ color:'#e65100', fontWeight:600, display:'block' }}>
                  Requiere verificación manual
                </Typography>
                <Typography variant="caption" sx={{ color:'#555' }}>
                  {resultado.mensajeAdicional}
                </Typography>
                {resultado.mensajeAdicional?.includes('contraloria.gov.co') && (
                  <Button size="small" variant="text"
                    onClick={() => window.open('https://www.contraloria.gov.co/web/guest/consulta-certificado', '_blank')}
                    sx={{ p:0, fontSize:'0.7rem', color:'#1565c0', mt:0.3, display:'block', textAlign:'left' }}>
                    🔗 Verificar en portal Contraloría →
                  </Button>
                )}
                {resultado.mensajeAdicional?.includes('judicial') && (
                  <Typography variant="caption" sx={{ color:'#888', display:'block', mt:0.5, fontStyle:'italic' }}>
                    El administrador revisará el nombre e identificación visualmente antes de aprobar.
                  </Typography>
                )}
                {resultado.mensajeAdicional?.includes('banco') && (
                  <Typography variant="caption" sx={{ color:'#888', display:'block', mt:0.5, fontStyle:'italic' }}>
                    El administrador verificará el sello y firma del banco antes de aprobar.
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Resultados por paso del backend */}
          {resultado?.pasos?.length > 0 && (
            <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
              {resultado.pasos.map(p => (
                <Tooltip key={p.paso} title={p.detalle} arrow>
                  <Chip
                    icon={iconoEstado(p.estado)}
                    label={p.paso.replace(/_/g, ' ')}
                    size="small"
                    sx={{
                      height: 22, fontSize: '0.63rem', cursor: 'default',
                      background: colorEstado(p.estado) + '18',
                      color: colorEstado(p.estado),
                      border: `1px solid ${colorEstado(p.estado)}44`,
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          )}

          {/* Datos leídos del código de barras */}
          {resultado?.codigoBarrasLeido && resultado?.datosCodigoBarras?.length > 0 && (
            <Box sx={{ mt: 1, p: 1, borderRadius: 1, background: '#e3f2fd', border: '1px solid #90caf9' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                <QrCodeIcon sx={{ fontSize: 14, color: AZUL }}/>
                <Typography variant="caption" sx={{ fontWeight: 700, color: AZUL }}>
                  Código leído del documento
                </Typography>
              </Box>
              {resultado.datosCodigoBarras.slice(0, 2).map((d, i) => (
                <Typography key={i} variant="caption" sx={{ color: '#555', display: 'block',
                  fontFamily: 'monospace', fontSize: '0.65rem',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.length > 80 ? d.substring(0, 80) + '…' : d}
                </Typography>
              ))}
            </Box>
          )}

          <Button size="small" variant="text" onClick={limpiar}
            sx={{ mt: 1.5, color: '#888', fontSize: '0.72rem', textDecoration: 'underline', p: 0 }}>
            Cambiar documento
          </Button>
        </Box>
      )}

      {/* ── INVÁLIDO ── */}
      {estado === 'invalido' && (
        <Box>
          <Alert severity="error" icon={<ErrorIcon/>} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>No se pudo verificar el documento</Typography>

            {/* Mensaje principal del backend */}
            {error && (
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                {error}
              </Typography>
            )}

            {/* Detalle de TODOS los pasos del backend */}
            {resultado?.pasos?.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                {resultado.pasos.map(p => {
                  const col = { OK:'#2e7d32', Error:'#c62828', Parcial:'#e65100',
                    Pendiente:'#888', Omitido:'#888', SinCodigo:'#e65100', SinTexto:'#e65100' }[p.estado] || '#888';
                  const ico = { OK:'✓', Error:'✗', Parcial:'⚠', Pendiente:'⏳', Omitido:'—',
                    SinCodigo:'⚠', SinTexto:'⚠' }[p.estado] || '•';
                  return (
                    <Typography key={p.paso} variant="caption"
                      sx={{ display: 'block', mt: 0.3, color: col }}>
                      {ico} <strong>{p.paso.replace(/_/g,' ')}:</strong> {p.detalle}
                    </Typography>
                  );
                })}
              </Box>
            )}

            {/* Si no hay resultado del backend (error de red) */}
            {!resultado && !error && (
              <Typography variant="caption" sx={{ display: 'block', color: '#c62828' }}>
                No se pudo contactar el servidor de verificación.
              </Typography>
            )}
          </Alert>
          <Button variant="outlined" color="error" size="small"
            startIcon={<CloudUploadIcon/>} onClick={limpiar}>
            Intentar con otro archivo
          </Button>
        </Box>
      )}

      <style>{`
        @keyframes pulseDoc {
          0%,100% { opacity:1; transform:scale(1); }
          50%      { opacity:0.4; transform:scale(0.85); }
        }
      `}</style>
    </Box>
  );
}
